import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { ColorVariant } from '../components/products/types';
import { getProxiedImageUrl, sanitizeUrl } from '../components/products/utils';

interface ImageCache {
  [url: string]: {
    status: 'loading' | 'loaded' | 'error';
    image?: HTMLImageElement;
    timestamp: number;
  };
}

interface UseImagePreloaderOptions {
  maxCacheSize?: number;
  cacheExpiryMs?: number;
  preloadPriority?: 'low' | 'high';
}

interface UseImagePreloaderReturn {
  preloadImage: (url: string) => Promise<boolean>;
  preloadAllVariants: (variants: ColorVariant[]) => Promise<void>;
  isImageLoaded: (url: string) => boolean;
  isImageLoading: (url: string) => boolean;
  getImageError: (url: string) => boolean;
  clearCache: () => void;
  cacheStats: {
    size: number;
    loadedCount: number;
    errorCount: number;
  };
  cacheVersion: number; // Version number that increments when cache changes, triggers re-renders
}

// Global image cache shared across all components
const globalImageCache: ImageCache = {};

// Track in-flight image loads globally so we can cancel them on demand
const activeImages = new Set<HTMLImageElement>();
const globalLoadingUrls = new Set<string>();
let preloadingPaused = false;

// Global cache version counter - shared across ALL hook instances
let globalCacheVersionCounter = 0;

// Global listeners for cache updates - all hook instances subscribe here
const cacheUpdateListeners = new Set<(version: number) => void>();

// Function to notify all listeners when cache changes
const notifyGlobalCacheUpdate = () => {
  globalCacheVersionCounter++;
  cacheUpdateListeners.forEach(listener => listener(globalCacheVersionCounter));
};

export const pauseAllImagePreloads = (): void => {
  if (preloadingPaused) return;
  preloadingPaused = true;
  cancelAllImagePreloads();
};

export const resumeImagePreloads = (): void => {
  preloadingPaused = false;
};

// Export a global cancel function to abort all in-flight image requests
export const cancelAllImagePreloads = (): void => {
  try {
    activeImages.forEach((img) => {
      try {
        // Detach handlers and abort loading
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (img as any).onload = null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (img as any).onerror = null;
        img.src = '';
      } catch {}
    });
  } catch {}
  activeImages.clear();
  globalLoadingUrls.clear();
  // Clear cache so UI falls back gracefully
  Object.keys(globalImageCache).forEach(url => delete globalImageCache[url]);
};

// Helper to clean expired cache entries
const cleanExpiredCache = (maxAge: number) => {
  const now = Date.now();
  Object.keys(globalImageCache).forEach(url => {
    if (now - globalImageCache[url].timestamp > maxAge) {
      delete globalImageCache[url];
    }
  });
};

export const useImagePreloader = (
  options: UseImagePreloaderOptions = {}
): UseImagePreloaderReturn => {
  const {
    maxCacheSize = 200,
    cacheExpiryMs = 30 * 60 * 1000, // 30 minutes
    preloadPriority = 'low'
  } = options;

  const [cacheVersion, setCacheVersion] = useState(globalCacheVersionCounter);
  const preloadQueueRef = useRef<Set<string>>(new Set());

  // Subscribe to global cache updates so ALL components re-render when ANY image loads
  useEffect(() => {
    const listener = (newVersion: number) => {
      setCacheVersion(newVersion);
    };
    
    cacheUpdateListeners.add(listener);
    
    return () => {
      cacheUpdateListeners.delete(listener);
    };
  }, []);

  // Clean cache periodically
  useEffect(() => {
    const interval = setInterval(() => {
      cleanExpiredCache(cacheExpiryMs);
      setCacheVersion(v => v + 1); // Trigger re-render to update stats
    }, 5 * 60 * 1000); // Clean every 5 minutes

    return () => clearInterval(interval);
  }, [cacheExpiryMs]);

  // Preload a single image
  const preloadImage = useCallback(async (rawUrl: string): Promise<boolean> => {
    if (preloadingPaused || !rawUrl) return false;

    // Process URL through sanitization and proxy
    const sanitizedUrl = sanitizeUrl(rawUrl);
    const url = getProxiedImageUrl(sanitizedUrl); // Use proxy URL for loading
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[useImagePreloader] preloadImage - rawUrl: ${rawUrl?.substring(0, 50)}..., proxied URL: ${url?.substring(0, 50)}...`);
    }
    
    if (!url) return false;

    // Check if already in cache
    if (globalImageCache[url]?.status === 'loaded') {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[useImagePreloader] Image already loaded from cache: ${url?.substring(0, 50)}...`);
      }
      return true;
    }

    // Check if already loading
    if (globalImageCache[url]?.status === 'loading' || preloadQueueRef.current.has(url)) {
      return new Promise((resolve) => {
        const checkLoaded = () => {
          if (globalImageCache[url]?.status === 'loaded') {
            resolve(true);
          } else if (globalImageCache[url]?.status === 'error') {
            resolve(false);
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
      });
    }

    // Add to preload queue
    preloadQueueRef.current.add(url);

    // Initialize cache entry
    globalImageCache[url] = {
      status: 'loading',
      timestamp: Date.now()
    };

    return new Promise((resolve) => {
      const img = new Image();
      activeImages.add(img);
      globalLoadingUrls.add(url);
      
      // Enable CORS for proxy requests with crossOrigin
      img.crossOrigin = 'anonymous';
      
      // Set loading priority based on option
      if (preloadPriority === 'low') {
        img.loading = 'lazy';
      }

      // Add timeout to detect stuck images
      const timeout = setTimeout(() => {
        if (globalImageCache[url]?.status === 'loading') {
          if (process.env.NODE_ENV === 'development') {
            console.warn(`[useImagePreloader] Image load TIMEOUT after 30s - URL: ${url?.substring(0, 50)}...`);
          }
          globalImageCache[url] = {
            status: 'error',
            timestamp: Date.now()
          };
          preloadQueueRef.current.delete(url);
          notifyGlobalCacheUpdate(); // Notify all components
          activeImages.delete(img);
          globalLoadingUrls.delete(url);
          resolve(false);
        }
      }, 30000); // 30 second timeout

      img.onload = () => {
        clearTimeout(timeout);
        globalImageCache[url] = {
          status: 'loaded',
          image: img,
          timestamp: Date.now()
        };
        preloadQueueRef.current.delete(url);
        if (process.env.NODE_ENV === 'development') {
          console.log(`[useImagePreloader] Image loaded - notifying all components, URL: ${url.substring(0, 50)}...`);
        }
        notifyGlobalCacheUpdate(); // Notify all components
        activeImages.delete(img);
        globalLoadingUrls.delete(url);
        resolve(true);
      };

      img.onerror = (error) => {
        clearTimeout(timeout);
        if (process.env.NODE_ENV === 'development') {
          console.error(`[useImagePreloader] Image load ERROR - URL: ${url?.substring(0, 50)}..., Error:`, error);
        }
        globalImageCache[url] = {
          status: 'error',
          timestamp: Date.now()
        };
        preloadQueueRef.current.delete(url);
        notifyGlobalCacheUpdate(); // Notify all components
        activeImages.delete(img);
        globalLoadingUrls.delete(url);
        resolve(false);
      };

      // Load through proxy with CORS support
      img.src = url;

      // Cleanup if cache gets too large
      if (Object.keys(globalImageCache).length > maxCacheSize) {
        const sortedEntries = Object.entries(globalImageCache)
          .sort((a, b) => a[1].timestamp - b[1].timestamp);
        
        // Remove oldest 20% of entries
        const toRemove = Math.floor(sortedEntries.length * 0.2);
        for (let i = 0; i < toRemove; i++) {
          delete globalImageCache[sortedEntries[i][0]];
        }
      }
    });
  }, [maxCacheSize, preloadPriority]);

  // Preload all color variant images
  const preloadAllVariants = useCallback(async (variants: ColorVariant[]): Promise<void> => {
    if (!variants || variants.length === 0 || preloadingPaused) return;

    const imageUrls = variants
      .map(variant => variant.image_url)
      .filter((url): url is string => Boolean(url));

    const swatchUrls = variants
      .map(variant => variant.swatch_url)
      .filter((url): url is string => Boolean(url));

    const allUrls = [...imageUrls, ...swatchUrls];

    // Preload all images concurrently with a reasonable batch size
    const batchSize = 6; // Don't overwhelm the browser
    for (let i = 0; i < allUrls.length; i += batchSize) {
      if (preloadingPaused) break;
      const batch = allUrls.slice(i, i + batchSize);
      await Promise.all(batch.map(url => preloadImage(url)));
      
      // Small delay between batches to prevent blocking the UI
      if (i + batchSize < allUrls.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
  }, [preloadImage]);

  // Check if image is loaded
  // Depend on cacheVersion so components re-render when images load
  const isImageLoaded = useCallback((rawUrl: string): boolean => {
    if (!rawUrl) return false;
    const sanitizedUrl = sanitizeUrl(rawUrl);
    const url = getProxiedImageUrl(sanitizedUrl);
    const isLoaded = url ? globalImageCache[url]?.status === 'loaded' : false;
    
    if (process.env.NODE_ENV === 'development' && url) {
      console.log(`[useImagePreloader] isImageLoaded - rawUrl: ${rawUrl?.substring(0, 50)}..., cacheKey: ${url?.substring(0, 50)}..., status: ${globalImageCache[url]?.status || 'not in cache'}, returning: ${isLoaded}`);
    }
    
    return isLoaded;
  }, [cacheVersion]);

  // Check if image is currently loading
  // Depend on cacheVersion so components re-render when loading state changes
  const isImageLoading = useCallback((rawUrl: string): boolean => {
    if (!rawUrl) return false;
    const sanitizedUrl = sanitizeUrl(rawUrl);
    const url = getProxiedImageUrl(sanitizedUrl);
    return url ? (
      globalImageCache[url]?.status === 'loading' || 
      preloadQueueRef.current.has(url)
    ) : false;
  }, [cacheVersion]);

  // Check if image failed to load
  // Depend on cacheVersion so components re-render when error state changes
  const getImageError = useCallback((rawUrl: string): boolean => {
    if (!rawUrl) return false;
    const sanitizedUrl = sanitizeUrl(rawUrl);
    const url = getProxiedImageUrl(sanitizedUrl);
    return url ? globalImageCache[url]?.status === 'error' : false;
  }, [cacheVersion]);

  // Clear cache
  const clearCache = useCallback(() => {
    Object.keys(globalImageCache).forEach(url => {
      delete globalImageCache[url];
    });
    preloadQueueRef.current.clear();
    notifyGlobalCacheUpdate(); // Notify all components
  }, []);

  // Get cache statistics (cacheVersion forces re-computation)
  const cacheStats = useMemo(() => {
    // Depend explicitly on cacheVersion so memo busts when cache mutates
    void cacheVersion;
    const entries = Object.values(globalImageCache);
    return {
      size: entries.length,
      loadedCount: entries.filter(entry => entry.status === 'loaded').length,
      errorCount: entries.filter(entry => entry.status === 'error').length
    };
  }, [cacheVersion]);

  return {
    preloadImage,
    preloadAllVariants,
    isImageLoaded,
    isImageLoading,
    getImageError,
    clearCache,
    cacheStats,
    cacheVersion // Export cacheVersion so components can trigger re-renders
  };
};
